import { useCallback, useEffect, useState } from 'react';
import { AuthContext } from './AuthProvider';
import axiosInstance, { SESSION_STORAGE } from '../utils/axiosInstance';
import Loader from '../components/Loader';
import OdooLogin from '../pages/OdooLogin';

/* Sign in with Odoo: the device holds an opaque session token; the backend
   verified the Odoo login and runs writes as that person. */
export default function OdooAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | login

  const readToken = () => {
    try {
      return localStorage.getItem(SESSION_STORAGE);
    } catch {
      return null;
    }
  };
  const clearToken = () => {
    try {
      localStorage.removeItem(SESSION_STORAGE);
    } catch {
      /* ignore */
    }
  };

  const load = useCallback(async () => {
    if (!readToken()) {
      setState('login');
      return;
    }
    setState('loading');
    try {
      const r = await axiosInstance.get('api/receiving/auth/session/');
      const u = r.data.user || {};
      setUser({ displayName: u.name, username: u.login, email: u.login, expiresAt: r.data.expires_at, isAdmin: Boolean(r.data.is_admin) });
      setState('ready');
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        clearToken();
        setState('login');
      } else {
        // network hiccup: keep the device usable, the API will say if the session is gone
        setUser((prev) => prev || { displayName: 'Signed in', username: '', email: '' });
        setState('ready');
      }
    }
  }, []);

  useEffect(() => {
    load();
    const onExpired = () => {
      clearToken();
      setUser(null);
      setState('login');
    };
    window.addEventListener('inny:unauthorized', onExpired);
    return () => window.removeEventListener('inny:unauthorized', onExpired);
  }, [load]);

  const signedIn = (payload) => {
    try {
      localStorage.setItem(SESSION_STORAGE, payload.token);
    } catch {
      /* ignore */
    }
    const u = payload.user || {};
    setUser({ displayName: u.name, username: u.login, email: u.login, expiresAt: payload.expires_at, isAdmin: Boolean(payload.is_admin) });
    setState('ready');
  };

  const logout = async () => {
    try {
      await axiosInstance.delete('api/receiving/auth/session/');
    } catch {
      /* the token is dropped either way */
    }
    clearToken();
    setUser(null);
    setState('login');
  };

  if (state === 'loading') return <Loader label="" />;
  if (state === 'login') return <OdooLogin onSignedIn={signedIn} />;
  return <AuthContext.Provider value={{ user, loading: false, logout }}>{children}</AuthContext.Provider>;
}
